import { createSelector, Selector } from '@ngxs/store';
import {
  CrlCategorizationFile,
  CrlCategorizationStatus,
  CrlDisplaySection,
  CrlItemCommentIds,
  CrlRequestItem,
  CrlUncategorizedCardVm,
} from '../models';
import { CxpCrlDetailSelectors, CrlSelectedRequestDisplay } from './cxp-crl-detail-state.selectors';
import { CxpCrlSubscriptionsStateModel } from './cxp-crl-subscriptions.model';
import { CxpCrlSubscriptionsState } from './cxp-crl-subscriptions.state';

export class CxpCrlSubscriptionsStateSelectors {
  @Selector([CxpCrlSubscriptionsState])
  static getCategorizationFiles(state: CxpCrlSubscriptionsStateModel): CrlCategorizationFile[] {
    return state.categorization.files;
  }

  /** Item ids the comments live query reports as having comments / unviewed comments. */
  @Selector([CxpCrlSubscriptionsState])
  static getItemCommentIds(state: CxpCrlSubscriptionsStateModel): CrlItemCommentIds {
    return state.comments;
  }

  /**
   * Whole-request live view model for the uncategorized-files card. `visible`
   * only while at least one settled uncategorized file exists — a file still
   * categorizing is counted in neither bucket, so a fresh drop keeps the card
   * hidden until the background process resolves.
   */
  @Selector([CxpCrlSubscriptionsStateSelectors.getCategorizationFiles])
  static getUncategorizedCardVm(files: CrlCategorizationFile[]): CrlUncategorizedCardVm {
    const uncategorizedFiles = files.filter((file) => file.status === CrlCategorizationStatus.UNCATEGORIZED);
    const categorizedCount = files.filter((file) => file.status === CrlCategorizationStatus.CATEGORIZED).length;

    return {
      visible: uncategorizedFiles.length > 0,
      categorizedCount,
      uncategorizedCount: uncategorizedFiles.length,
      files: uncategorizedFiles,
    };
  }

  /**
   * The list's sections with each row's live comment state folded in — the join
   * between the loaded `/page` detail and the comments live query, which sit in
   * two different states.
   *
   * The flags are written onto the item rather than passed alongside it so every
   * add-on builder stays a pure function of one `CrlRequestItem`
   * (`CrlItemAddonBuilder`), and so anything already rendering a row picks the
   * comment add-on up for free. A guest never opens the live query, so both id
   * lists stay empty and no row shows the add-on.
   */
  @Selector([CxpCrlDetailSelectors.getSelectedRequestDisplay, CxpCrlSubscriptionsStateSelectors.getItemCommentIds])
  static getDisplaySectionsWithComments(
    display: CrlSelectedRequestDisplay,
    commentIds: CrlItemCommentIds,
  ): CrlDisplaySection[] {
    const withComments = new Set(commentIds.withComments);
    if (withComments.size === 0) return display.displaySections;

    const withUnviewed = new Set(commentIds.withUnviewed);
    const applyComments = (item: CrlRequestItem): CrlRequestItem =>
      withComments.has(item.id) ? { ...item, hasComments: true, hasUnviewedComments: withUnviewed.has(item.id) } : item;

    return display.displaySections.map((section) => ({
      ...section,
      openItems: section.openItems.map(applyComments),
      finishedItems: section.finishedItems.map(applyComments),
    }));
  }

  static getCategorizationFileByFileId(
    fileId: number,
  ): (files: CrlCategorizationFile[]) => CrlCategorizationFile | null {
    return createSelector(
      [CxpCrlSubscriptionsStateSelectors.getCategorizationFiles],
      (files: CrlCategorizationFile[]) => files.find((file) => file.fileId === fileId) ?? null,
    );
  }
}
